import * as THREE from 'three';

/*
 * Flyby - a one-shot cinematic pass along the ship.
 *
 * Two points are chosen around the hull, each offset randomly:
 *
 *   fore - out past the bow
 *   aft  - out past the stern
 *
 * The camera tweens from wherever it is now, through both, easing its aim off the
 * view the user already had and onto a point inside the ship's bounding box. It
 * holds there for a beat, then snaps back to the exact camera and OrbitControls
 * state it started from. The first frame therefore matches the view the button was
 * pressed from, in both position and aim.
 *
 * The route is a CatmullRomCurve3, so the camera turns smoothly through the
 * waypoints instead of cornering at each one, and it is flown with a single eased
 * parameter. Both waypoints sit off the same flank, picked at random each time so
 * flybys come from either side, and `clearance` diagonals off the centreline so the
 * run between them passes beside the hull rather than through it.
 *
 * The flight is driven by a single smootherstep, so the camera eases out of its
 * resting view and eases back to a standstill on the last waypoint before holding
 * for the pause and snapping back.
 *
 * Caution: Curve.getPointAt() turns u > 1 into NaN (its arc length binary search
 * walks off the end of the table), so the eased parameter goes through _ease(),
 * which clamps it.
 */

export const FLYBY_DEFAULTS = {
  duration: 15.0,   // seconds to fly the route
  pause: 0.0,      // seconds to hold on the last point before snapping back
  // How far off the ship's centreline the route runs, in ship diagonals. The ship's
  // bounding sphere is 0.5, so above that the route passes beside the hull. Below it
  // the route cuts through the ship - the fallback that normally keeps the camera off
  // the hull cannot rescue a clearance that is itself inside it - which is only
  // useful for seeing exactly where the route goes.
  clearance: 0.62,
};

export default class Flyby {

  constructor({
    camera,
    controls,
    params = {},
    onStateChange = null,
  } = {}) {
    this.camera = camera;
    this.controls = controls;
    this.params = Object.assign( {}, FLYBY_DEFAULTS, params );
    this.onStateChange = onStateChange;

    this.active = false;
    this.time = 0;
    this.lastElapsed = null;
    this.progress = 0;                 // 0..1, for external displays
    this.curve = null;

    this.center = new THREE.Vector3();  // ship bounding box centre
    this.boxAim = new THREE.Vector3();  // point in the box the flyby aims at
    this.aim = new THREE.Vector3();     // the live look point, blended onto boxAim

    // everything needed to put the camera and the controls back as they were
    this.saved = {
      position: new THREE.Vector3(),
      quaternion: new THREE.Quaternion(),
      up: new THREE.Vector3( 0, 1, 0 ),
      target: new THREE.Vector3(),
      controlsEnabled: true,
    };

    this._point = new THREE.Vector3();
  }

  isActive() {
    return this.active;
  }

  /**
   * Start a flyby.
   *
   * @param {object} args
   * @param {THREE.Vector3} args.center ship bounding box centre
   * @param {THREE.Vector3} args.size   ship bounding box size, for scaling the route
   * @returns {boolean} true if the flyby started
   */
  start({ center, size } = {}) {
    if ( ! this.camera || ! this.controls || ! center || ! size ) return false;

    this.saved.position.copy( this.camera.position );
    this.saved.quaternion.copy( this.camera.quaternion );
    this.saved.up.copy( this.camera.up );
    this.saved.target.copy( this.controls.target );
    this.saved.controlsEnabled = this.controls.enabled;

    this.center.copy( center );
    this._buildRoute( size );

    this.time = 0;
    this.lastElapsed = null;
    this.progress = 0;
    this.active = true;

    this.controls.enabled = false;   // OrbitControls would fight us for the camera

    if ( this.onStateChange ) this.onStateChange( true );

    return true;
  }

  /** Stop early and put the camera back where it started. */
  cancel() {
    if ( ! this.active ) return;
    this.active = false;
    this.curve = null;
    this._restore();
    if ( this.onStateChange ) this.onStateChange( false );
  }

  /**
   * Fly the route, hold, then snap back. Called once per frame with the scene's
   * elapsed time.
   *
   * @param {number} elapsed clock elapsed seconds
   */
  update( elapsed ) {
    if ( ! this.active || ! this.curve ) return;

    // the host consumes clock.getDelta() elsewhere and getDelta() returns ~0 when
    // called twice in a row, so derive the step from the monotonic clock
    const dt = this.lastElapsed === null
      ? 0
      : Math.min( Math.max( elapsed - this.lastElapsed, 0 ), 0.25 );
    this.lastElapsed = elapsed;
    this.time += dt;

    const duration = Math.max( this.params.duration, 0.1 );

    let travelled = 0;   // 0 = the user's own view, 1 = the flyby's aim point

    if ( this.time < duration ) {
      this.progress = this.time / duration;
      // one ease over the flight: the camera accelerates out of its current view
      // and settles onto the last point
      travelled = this._ease( this.progress );
      this.curve.getPointAt( travelled, this._point );
    } else if ( this.time < duration + Math.max( this.params.pause, 0 ) ) {
      this.progress = 1;
      travelled = 1;
      this.curve.getPointAt( 1, this._point );   // hold on the last point
    } else {
      this.active = false;
      this.curve = null;
      this.progress = 1;
      this._restore();                            // snap back
      if ( this.onStateChange ) this.onStateChange( false );
      return;
    }

    // Swing the aim off the view the user was already looking at and onto the
    // flyby's aim point. Because both ends are the camera's own position, this is
    // what makes the very first frame of the flyby identical to the view it was
    // launched from, instead of twisting to a new target straight away.
    this.aim.lerpVectors( this.saved.target, this.boxAim, travelled );

    this.camera.position.copy( this._point );
    this.camera.up.set( 0, 1, 0 );

    // lookAt() aims from the camera's matrixWorld, which only gets recomputed when
    // the renderer draws, so refresh it here or the camera would aim from wherever
    // it was on the previous frame
    this.camera.updateMatrixWorld();
    this.camera.lookAt( this.aim );
  }

  // --- live parameters ------------------------------------------------------

  setDuration( value ) {
    const next = THREE.MathUtils.clamp( value, 1, 40 );
    // rescale the elapsed time so dragging the slider mid-flight does not jump
    if ( this.active && this.params.duration > 0 ) {
      this.time *= next / this.params.duration;
    }
    this.params.duration = next;
  }

  setPause( value ) {
    this.params.pause = THREE.MathUtils.clamp( value, 0, 10 );
  }

  setClearance( value ) {
    this.params.clearance = THREE.MathUtils.clamp( value, 0, 3 );
  }

  // --- internals ------------------------------------------------------------

  /**
   * smootherstep clamped into [0,1]: the camera eases out of its resting view and
   * eases back to a standstill on the last waypoint, so the flight neither starts
   * nor stops abruptly.
   *
   * The clamp matters: three's smootherstep can return a hair over 1 for an input a
   * hair under 1, and Curve.getPointAt() turns any u > 1 into NaN (its arc length
   * binary search walks off the end of the table).
   */
  _ease( x ) {
    return THREE.MathUtils.clamp( THREE.MathUtils.smootherstep( x, 0, 1 ), 0, 1 );
  }

  /**
   * Pick a waypoint past the bow and one past the stern, then thread a curve from
   * the camera through both.
   *
   * Both sit off the same flank, `clearance` diagonals off the centreline, so the run
   * between them passes alongside the hull rather than through it. The flank is
   * picked at random so repeated flybys come from both sides, but a route that would
   * pass through the ship - which happens when the camera is on the far side - falls
   * back to the other flank, or to whichever of the two is roomier.
   */
  _buildRoute( size ) {
    const diagonal = Math.max( size.length(), 1e-3 );
    const hull = 0.5 * diagonal;                    // ship bounding sphere radius
    const forward = new THREE.Vector3( 0, 0, 1 );   // the bow faces +Z (the Front view)

    // a value somewhere in -amount..amount
    const jitter = ( amount ) => ( Math.random() * 2 - 1 ) * amount;

    const offset = this.saved.position.clone().sub( this.center );
    // across the ship's centreline. +X is the ship's port side in this app: the
    // "Right" view is the one that sits at bbCenter.x minus the view distance.
    const across = new THREE.Vector3( 0, 1, 0 ).cross( forward ).normalize();
    const cameraSide = across.dot( offset ) < 0 ? - 1 : 1;

    // The quadrant this pass sweeps, chosen up front so the whole flyby clearly
    // runs down one side of the hull and either over the top or under the bottom.
    // Both are coin flips, so repeated flybys cover all four quadrants.
    const side = Math.random() < 0.5 ? 1 : - 1;
    const liftSign = Math.random() < 0.5 ? 1 : - 1;

    const along = diagonal * ( 1.0 + Math.random() * 0.6 );    // out past bow and stern
    // How far the waypoints sit off the ship's centreline. The straight run between
    // them keeps this distance, and that run is the closest the route comes to the
    // ship, so this is what keeps the camera off the hull.
    const lateral = diagonal * ( this.params.clearance + Math.random() * 0.25 );
    // One shared lift plus a small per-point wobble, so the pass stays coherently
    // above or below the ship instead of the two ends cancelling each other out.
    const lift = diagonal * ( 0.22 + Math.random() * 0.22 ) * liftSign;
    const liftFore = lift + jitter( 0.1 ) * diagonal;
    const liftAft = lift + jitter( 0.1 ) * diagonal;

    // look at a random point inside the ship's bounding box
    this.boxAim.copy( this.center ).add( new THREE.Vector3(
      jitter( 0.5 ) * size.x, jitter( 0.5 ) * size.y, jitter( 0.5 ) * size.z
    ) );

    const routeFor = ( flank ) => {
      const fore = this.center.clone()
        .addScaledVector( forward, along )
        .addScaledVector( across, lateral * flank )
        .add( new THREE.Vector3( 0, liftFore, 0 ) );
      const aft = this.center.clone()
        .addScaledVector( forward, - along )
        .addScaledVector( across, lateral * flank )
        .add( new THREE.Vector3( 0, liftAft, 0 ) );
      // run past whichever end of the ship is nearer first, so the camera never
      // sets off the long way round
      const bowFirst = this.saved.position.distanceTo( fore )
        <= this.saved.position.distanceTo( aft );
      return {
        fore,
        aft,
        points: [
          this.saved.position.clone(),
          bowFirst ? fore : aft,
          bowFirst ? aft : fore,
        ],
      };
    };

    const clearanceOf = ( points ) => {
      const curve = new THREE.CatmullRomCurve3( points, false, 'centripetal' );
      let closest = Infinity;
      for ( let i = 1; i < 96; i ++ ) {
        curve.getPointAt( i / 96, this._point );
        closest = Math.min( closest, this._point.distanceTo( this.center ) );
      }
      return closest;
    };

    const flanks = side === cameraSide ? [ side, - side ] : [ side, cameraSide ];

    let chosen = null;
    let roomiest = - 1;
    for ( const flank of flanks ) {
      const candidate = routeFor( flank );
      const clearance = clearanceOf( candidate.points );
      if ( clearance > roomiest ) {
        roomiest = clearance;
        chosen = candidate;
      }
      if ( clearance >= hull ) break;
    }

    this.waypoints = { fore: chosen.fore, aft: chosen.aft };
    this.curve = new THREE.CatmullRomCurve3( chosen.points, false, 'centripetal' );
    this.curve.arcLengthDivisions = 300;
  }

  /** Put the camera and the controls back exactly as they were. */
  _restore() {
    if ( ! this.camera || ! this.controls ) return;

    this.camera.position.copy( this.saved.position );
    this.camera.quaternion.copy( this.saved.quaternion );
    this.camera.up.copy( this.saved.up );
    this.controls.target.copy( this.saved.target );
    this.controls.enabled = this.saved.controlsEnabled;
  }
}
